import { useEffect, useState } from "react";
import { useRouter } from "next/router";
import { Dialog, DialogContent, DialogTitle, Divider, IconButton, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Switch, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import { useRecoilValue } from "recoil";
import { textColorState, userState } from "@/lib/store";
import { getHelpMenu, HelpMenuEntry } from "@/controllers/tutorials";
import { areToursDisabled, setToursDisabled, startTour } from "@/components/tour/TourGuide";
import { usePageTour } from "@/components/tour/usePageTour";

/**
 * The Help dialog (Appcues replacement) — driven by our own tutorials.
 *
 * Lists the guided tours the backend says this user may launch
 * (GET /interface/api/tutorials/ — audience-gated server-side, content
 * managed in Django admin). An entry's href is the page its tour runs
 * on (with a ?tour=<id> param that starts it on arrival); entries
 * without an href — and entries for the page we're already on — start
 * the tour in place.
 *
 * The listing only covers tours whose page can be linked without a ticker, so
 * it can never carry the stock-tab tours. Those are reached by the "this page"
 * entry at the top, which comes from the route rather than the listing and so
 * covers every page that has a tour at all.
 */
interface Props {
    open: boolean;
    onClose: () => void;
}

const HelpMenu = ({ open, onClose }: Props) => {
    const router = useRouter();
    const user = useRecoilValue(userState);
    // Match the surrounding text rather than MUI's muted icon default —
    // white on the dark theme, dark on the light one.
    const textColor = useRecoilValue(textColorState);
    // The tour for the page behind this dialog, whether or not it can be
    // listed below — this is the only route in for the stock tabs.
    const pageTour = usePageTour();
    const [entries, setEntries] = useState<HelpMenuEntry[]>([]);
    const [loaded, setLoaded] = useState(false);
    // The global "show guides automatically" switch — the discoverable,
    // reversible home of the opt-out set from any tour card.
    const [guidesOn, setGuidesOn] = useState(true);

    useEffect(() => {
        if (!open) return;
        setGuidesOn(!areToursDisabled(user));
        let cancelled = false;
        getHelpMenu()
            .then((res) => {
                if (!cancelled) setEntries(res.tutorials);
            })
            .catch(() => {
                if (!cancelled) setEntries([]);
            })
            .finally(() => {
                if (!cancelled) setLoaded(true);
            });
        return () => {
            cancelled = true;
        };
    }, [open, user]);

    const openEntry = (entry: HelpMenuEntry) => {
        onClose();
        const path = entry.href?.split("?")[0];
        if (!entry.href || path === router.asPath.split("?")[0]) {
            // No page link, or already on the tour's page (where a
            // same-URL push wouldn't re-fire the ?tour= launcher) —
            // start it right here.
            startTour(entry.id);
        } else {
            router.push(entry.href);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ pr: 6 }}>
                Help &amp; guided tours
                <IconButton aria-label="Close" onClick={onClose} sx={{ position: "absolute", top: 8, right: 8 }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: 0 }}>
                <Typography variant="body2" sx={{ opacity: 0.8, mb: 1 }}>
                    Short guided walkthroughs of ORTEX, right on the page they explain.
                </Typography>
                {pageTour && (
                    <>
                        <List dense>
                            <ListItemButton
                                onClick={() => {
                                    onClose();
                                    pageTour.start();
                                }}
                            >
                                <ListItemIcon sx={{ color: textColor }}>
                                    <PlayCircleOutlineIcon />
                                </ListItemIcon>
                                <ListItemText primary="Show me around this page" secondary="The guided tour for the page you're on" />
                            </ListItemButton>
                        </List>
                        <Divider sx={{ my: 1 }} />
                    </>
                )}
                <List dense>
                    {entries
                        .filter((entry) => entry.id !== pageTour?.tourId)
                        .map((entry) => (
                            <ListItemButton key={entry.id} onClick={() => openEntry(entry)}>
                                <ListItemIcon sx={{ color: textColor }}>
                                    <HelpOutlineIcon />
                                </ListItemIcon>
                                <ListItemText primary={entry.label} />
                            </ListItemButton>
                        ))}
                    {loaded && entries.length === 0 && (
                        <Typography variant="body2" sx={{ opacity: 0.7, px: 2, py: 1 }}>
                            No guided tours are available for your account yet.
                        </Typography>
                    )}
                </List>
                <Divider sx={{ my: 1 }} />
                <List dense>
                    <ListItem
                        secondaryAction={
                            <Switch
                                edge="end"
                                checked={guidesOn}
                                onChange={(e) => {
                                    setGuidesOn(e.target.checked);
                                    setToursDisabled(!e.target.checked, user);
                                }}
                                inputProps={{ "aria-label": "Show guides automatically" }}
                            />
                        }
                    >
                        <ListItemText
                            primary="Show guides automatically"
                            secondary={guidesOn ? "New pages introduce themselves once" : "Off: start any guide from this menu"}
                        />
                    </ListItem>
                </List>
            </DialogContent>
        </Dialog>
    );
};

export default HelpMenu;
